import React from 'react';
import { formatDateOnly, formatLocalDate, parseLocalDate, getWeekday } from '../utils/dateUtils';

const MeetingSummaryModal = ({ meeting, onClose }) => {
  if (!meeting) return null;

  const calculateGoogleConversionRate = () => {
    const contactsGoogle = meeting.contactsGeneratedGoogle || 0;
    const patientsGoogle = meeting.patientsGoogle || 0;
    if (contactsGoogle === 0) return '0%';
    const rate = (patientsGoogle / contactsGoogle) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const calculateFacebookConversionRate = () => {
    const contactsFacebook = meeting.contactsGeneratedFacebook || 0;
    const patientsFacebook = meeting.patientsFacebook || 0;
    if (contactsFacebook === 0) return '0%';
    const rate = (patientsFacebook / contactsFacebook) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const calculateTotalConversionRate = () => {
    const totalContacts = (meeting.contactsGeneratedGoogle || 0) + (meeting.contactsGeneratedFacebook || 0);
    const totalPatients = (meeting.patientsGoogle || 0) + (meeting.patientsFacebook || 0);
    if (totalContacts === 0) return '0%';
    const rate = (totalPatients / totalContacts) * 100;
    return `${rate.toFixed(1)}%`;
  };


  const generateExecutiveSummary = () => {
    // Si ya existe una minuta ejecutiva completa (con historial), usarla
    if (meeting.executiveSummary) {
      return meeting.executiveSummary;
    }

    // Fallback: generar minuta básica si no existe la completa
    const meetingDate = parseLocalDate(meeting.date);
    const formattedDate = formatLocalDate(meetingDate, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const clientNameUpper = meeting.clientName ? meeting.clientName.toUpperCase() : 'CLIENTE DESCONOCIDO';

    // Calcular totales y tasas de conversión
    const totalContactsGoogle = meeting.contactsGeneratedGoogle || 0;
    const totalPatientsGoogle = meeting.patientsGoogle || 0;
    const totalContactsFacebook = meeting.contactsGeneratedFacebook || 0;
    const totalPatientsFacebook = meeting.patientsFacebook || 0;
    const totalContacts = totalContactsGoogle + totalContactsFacebook;
    const totalPatients = totalPatientsGoogle + totalPatientsFacebook;

    return `MINUTA EJECUTIVA - ${clientNameUpper}\n` +
           `Fecha: ${formattedDate}\n\n` +
           `___________________________________________\n\n` +
           `MÉTRICAS DE RENDIMIENTO POR PLATAFORMA:\n\n` +
           `GOOGLE ADS:\n` +
           `• Contactos generados: ${totalContactsGoogle}\n` +
           `• Pacientes concretados: ${totalPatientsGoogle}\n` +
           `• Tasa de conversión: ${calculateGoogleConversionRate()}\n\n` +
           `FACEBOOK ADS:\n` +
           `• Contactos generados: ${totalContactsFacebook}\n` +
           `• Pacientes concretados: ${totalPatientsFacebook}\n` +
           `• Tasa de conversión: ${calculateFacebookConversionRate()}\n\n` +
           `TOTALES GENERALES:\n` +
           `• Total contactos generados: ${totalContacts}\n` +
           `• Total pacientes concretados: ${totalPatients}\n` +
           `• Tasa de conversión general: ${calculateTotalConversionRate()}\n\n` +
           `RESUMEN EJECUTIVO:\n${meeting.observations}\n\n` +
           `ACCIONES:\n${meeting.agreements || 'No se registraron acciones específicas'}\n\n` +
           (meeting.nextMeetingDate ? `PRÓXIMA JUNTA DE SEGUIMIENTO: ${getWeekday(meeting.nextMeetingDate)}, ${formatDateOnly(meeting.nextMeetingDate)}\n\n` : '') +
           `___________________________________________\n` +
           `Generado el ${formatDateOnly(new Date())}`;
  }

  const generateQuickMinuta = () => {
    const nextMeetingDate = meeting.nextMeetingDate ? 
      `${getWeekday(meeting.nextMeetingDate)}, ${formatDateOnly(meeting.nextMeetingDate)}` : 
      'Pendiente';
    const clientName = meeting.clientName || 'Cliente Desconocido';

    // Calcular totales
    const totalContactsGoogle = meeting.contactsGeneratedGoogle || 0;
    const totalPatientsGoogle = meeting.patientsGoogle || 0;
    const totalContactsFacebook = meeting.contactsGeneratedFacebook || 0;
    const totalPatientsFacebook = meeting.patientsFacebook || 0;
    const totalContacts = totalContactsGoogle + totalContactsFacebook;
    const totalPatients = totalPatientsGoogle + totalPatientsFacebook;

    return `Fecha de la junta: ${formatDateOnly(meeting.date)}\n` +
           `Cliente: ${clientName}\n\n` +
           `Resumen ejecutivo por plataforma\n` +
           `Google Ads:\n` +
           `• Contactos generados: ${totalContactsGoogle}\n` +
           `• Pacientes concretados: ${totalPatientsGoogle}\n` +
           `• Tasa de conversión: ${calculateGoogleConversionRate()}\n\n` +
           `Facebook Ads:\n` +
           `• Contactos generados: ${totalContactsFacebook}\n` +
           `• Pacientes concretados: ${totalPatientsFacebook}\n` +
           `• Tasa de conversión: ${calculateFacebookConversionRate()}\n\n` +
           `Totales generales:\n` +
           `• Total contactos: ${totalContacts}\n` +
           `• Total pacientes: ${totalPatients}\n` +
           `• Tasa de conversión general: ${calculateTotalConversionRate()}\n\n` +
           `Observaciones: ${meeting.observations}\n\n` +
           `Acciones acordadas\n${meeting.agreements || 'No se registraron acciones específicas'}\n\n` +
           `Próxima junta de seguimiento: ${nextMeetingDate}`;
  };

  const executiveSummary = generateExecutiveSummary();
  const quickMinuta = generateQuickMinuta();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-center mb-4">
            <img 
              src="https://4tsix0yujj.ufs.sh/f/2vMRHqOYUHc0ruupdRNQiYtyo2GFJjxB80HOpNwcWZhvnAD4" 
              alt="Logo" 
              className="h-16 mb-4"
            />
          </div>
          
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-[#0061A8]">Minuta Ejecutiva</h2>
            <button 
              onClick={onClose}
              className="text-[#63666A] hover:text-[#0061A8]"
            >
              ✕
            </button>
          </div>

          <div className="bg-[#D1D3D4] p-4 rounded-md mb-4">
            <pre className="whitespace-pre-wrap font-sans text-[#63666A]">{executiveSummary}</pre>
          </div>

          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => {
                navigator.clipboard.writeText(executiveSummary);
                alert("Minuta ejecutiva copiada al portapapeles");
              }}
              className="bg-[#D1D3D4] text-[#63666A] py-2 px-4 rounded-md hover:bg-opacity-80 transition-colors"
            >
              Copiar Minuta Ejecutiva
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(quickMinuta);
                alert("Minuta rápida copiada al portapapeles");
              }}
              className="bg-[#A8D08D] text-[#FFFFFF] py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
            >
              Generar Minuta Rápida
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingSummaryModal;
